import { useHandler } from "@redotech/react-util/hook";
import {
  FrequencyCap,
  FrequencyCapMode,
  TriggerStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { WaitTimeUnit } from "@redotech/redo-flows-common/steps";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType, StepTypeDetailsProps } from "../../steps";

/** The radio offers the choice that stores nothing: no cap at all. */
const NO_LIMIT = "NO_LIMIT";
type FrequencyCapChoice = FrequencyCapMode | typeof NO_LIMIT;

const DEFAULT_COOLDOWN = { value: 14, unit: WaitTimeUnit.DAYS };

const timeUnitOptions: RedoListItem<WaitTimeUnit>[] = Object.values(
  WaitTimeUnit,
).map((unit) => ({ id: unit, type: "text", text: unit, value: unit }));

export const FrequencyCapInspector = memo(function FrequencyCapInspector({
  step,
  setStep,
}: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>) {
  const { t } = useTranslation();
  const frequencyCap = step.frequencyCap;
  const storedCooldown =
    frequencyCap?.mode === FrequencyCapMode.COOLDOWN ? frequencyCap : undefined;
  const cooldown = {
    value: storedCooldown?.value ?? DEFAULT_COOLDOWN.value,
    unit: storedCooldown?.unit ?? DEFAULT_COOLDOWN.unit,
  };

  const setFrequencyCap = useHandler((frequencyCap: FrequencyCap | null) => {
    setStep({ ...step, frequencyCap });
  });

  const setChoice = useHandler((choice: FrequencyCapChoice) => {
    switch (choice) {
      case NO_LIMIT:
        return setFrequencyCap(null);
      case FrequencyCapMode.NO_REENTRY:
        return setFrequencyCap({ mode: FrequencyCapMode.NO_REENTRY });
      case FrequencyCapMode.COOLDOWN:
        return setFrequencyCap({
          mode: FrequencyCapMode.COOLDOWN,
          ...DEFAULT_COOLDOWN,
        });
    }
  });

  const options: RadioGroupOption<FrequencyCapChoice>[] = useMemo(
    () => [
      {
        id: NO_LIMIT,
        label: t("Allow re-entry"),
        description: t("Customers enter each time they meet the conditions."),
      },
      {
        id: FrequencyCapMode.NO_REENTRY,
        label: t("Don't allow re-entry"),
        description: t("Customers can enter this automation only once."),
      },
      {
        id: FrequencyCapMode.COOLDOWN,
        label: t("Allow re-entry after a time period"),
        description: t(
          "Customers can enter again when the selected interval has elapsed.",
        ),
        childrenRenderLocation: "bottom",
        children: (
          <Flex gap="md">
            <RedoIncrementDecrement
              min={1}
              setValue={(value) =>
                setFrequencyCap({
                  mode: FrequencyCapMode.COOLDOWN,
                  unit: cooldown.unit,
                  value: value || 1,
                })
              }
              size="small"
              value={cooldown.value}
            />
            <RedoSingleSelectDropdownInput<WaitTimeUnit>
              fitToAnchor={false}
              options={timeUnitOptions}
              optionSelected={({ item }) =>
                setFrequencyCap({
                  mode: FrequencyCapMode.COOLDOWN,
                  unit: item.value,
                  value: cooldown.value,
                })
              }
              selectedItem={timeUnitOptions.find((o) => o.id === cooldown.unit)}
              size="xs"
            />
          </Flex>
        ),
      },
    ],
    [cooldown.unit, cooldown.value, setFrequencyCap, t],
  );

  return (
    <Flex dir="column" gap="xl">
      <Flex dir="column" gap="md">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Re-entry rules")}
        </Text>
        <Text fontSize="xxs" textColor="secondary">
          {t("Choose when a customer can start this automation again.")}
        </Text>
      </Flex>
      <RedoRadioGroup<FrequencyCapChoice>
        options={options}
        setValue={setChoice}
        size="sm"
        value={frequencyCap?.mode ?? NO_LIMIT}
      />
    </Flex>
  );
});
